/**
 * Derives the current state of a collaboration, including:
 * - Which milestone is currently active
 * - Who needs to take the next action (athlete or marketer)
 * - What action is required
 *
 * This utility is used by both frontend apps to display consistent status information.
 */

export interface Submission {
  id: string
  status: 'pending_review' | 'approved' | 'revision_requested' | 'superseded'
  [key: string]: any
}

export interface MilestoneCheckIn {
  active: boolean
  response: 'on_track' | 'needs_more_time' | null
  responseAt: string | null
}

/** Steps the brand does itself; the rest are the athlete's (see shared-db/milestoneTypes) */
export const BRAND_STEP_SLUGS = [
  'product_shipped',
  'prepare_appearance',
  'brand_confirms',
  'brand_approves',
]
export const isBrandStep = (m: Record<string, any> | null | undefined) =>
  !!m && BRAND_STEP_SLUGS.includes(String(m.slug ?? m.type_slug ?? ''))

export interface Milestone {
  id: string
  name: string
  status: 'pending' | 'in_progress' | 'submitted' | 'revision_requested' | 'approved' | 'completed'
  requiresApproval?: boolean
  requires_approval?: boolean
  sequenceOrder?: number
  sequence_order?: number
  dueDate?: string
  due_date?: string
  submissions?: Submission[]
  checkIn?: MilestoneCheckIn | null
  [key: string]: any
}

const formatCheckInRelative = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export interface Collaboration {
  id?: string | number
  // bid_status should only be: 'offered', 'accepted', 'rejected', 'expired'
  // Other statuses like 'live' or 'completed' are legacy and should be derived from milestones
  status: 'offered' | 'accepted' | 'rejected' | 'expired' | 'live' | 'completed'
  milestones?: Milestone[]
  [key: string]: any
}

export interface CollaborationState {
  currentMilestone: Milestone | null
  actionOwner: 'athlete' | 'marketer' | null
  actionRequired: string | null
  waitingOn: 'athlete' | 'marketer' | null
  isComplete: boolean
  pendingReviewMilestone: Milestone | null
  milestones?: Milestone[]
}

export interface StatusDisplay {
  title: string
  description: string
  actionButton: string | null
  variant: 'success' | 'waiting' | 'action' | 'unknown'
}

/**
 * Derive the current state of a collaboration
 */
export function deriveCollaborationState(collaboration: Collaboration | null): CollaborationState {
  const milestones = collaboration?.milestones || []
  const status = collaboration?.status

  // If collaboration hasn't been accepted yet
  if (status === 'offered') {
    return {
      currentMilestone: null,
      actionOwner: 'athlete',
      actionRequired: 'Accept or decline the offer',
      waitingOn: 'athlete',
      isComplete: false,
      pendingReviewMilestone: null,
      milestones,
    }
  }

  if (status === 'rejected') {
    return {
      currentMilestone: null,
      actionOwner: null,
      actionRequired: null,
      waitingOn: null,
      isComplete: false,
      pendingReviewMilestone: null,
      milestones,
    }
  }

  // For expired offers
  if (status === 'expired') {
    return {
      currentMilestone: null,
      actionOwner: null,
      actionRequired: null,
      waitingOn: null,
      isComplete: false,
      pendingReviewMilestone: null,
      milestones,
    }
  }

  // Note: 'live' and 'completed' are legacy values that shouldn't be in bid_status
  // They are derived from milestones, but we handle them for backwards compatibility
  if (status === 'completed' || status === 'live') {
    return {
      currentMilestone: null,
      actionOwner: null,
      actionRequired: 'Collaboration complete',
      waitingOn: null,
      isComplete: true,
      pendingReviewMilestone: null,
      milestones,
    }
  }

  // Sort milestones by sequence order
  const sortedMilestones = [...milestones].sort(
    (a, b) =>
      (a.sequenceOrder || a.sequence_order || 0) - (b.sequenceOrder || b.sequence_order || 0),
  )

  // Find the current active milestone (first one not approved/completed)
  const currentMilestone =
    sortedMilestones.find((m) => m.status !== 'approved' && m.status !== 'completed') || null

  // Check if any milestone has a pending review submission

  sortedMilestones.find((m) => {
    const subs = m.submissions || []
    return subs.some((s) => s.status === 'pending_review')
  }) || null

  // If there are no milestones, but status is not completed/live, it means we are waiting for milestones to be added
  if (sortedMilestones.length === 0) {
    return {
      currentMilestone: null,
      actionOwner: null,
      actionRequired: 'Waiting for campaign setup',
      waitingOn: null,
      isComplete: false,
      pendingReviewMilestone: null,
      milestones: [],
    }
  }

  if (!currentMilestone) {
    return {
      currentMilestone: null,
      actionOwner: null,
      actionRequired: 'Collaboration complete',
      waitingOn: null,
      isComplete: true,
      pendingReviewMilestone: null,
      milestones: sortedMilestones,
    }
  }

  // Check if there's a pending submission to review.
  // Also treat milestone status 'submitted' as pending review — the list API does not
  // hydrate the submissions array, so we must infer from the milestone status itself.
  const hasPendingSubmission =
    currentMilestone.status === 'submitted' ||
    currentMilestone.submissions?.some((s) => s.status === 'pending_review')

  if (hasPendingSubmission) {
    return {
      currentMilestone,
      actionOwner: 'marketer',
      actionRequired: `Review submission for "${currentMilestone.name}"`,
      waitingOn: 'marketer',
      isComplete: false,
      pendingReviewMilestone: currentMilestone,
      milestones: sortedMilestones,
    }
  }

  if (currentMilestone.status === 'revision_requested') {
    return {
      currentMilestone,
      actionOwner: 'athlete',
      actionRequired: `Revise the submission for "${currentMilestone.name}"`,
      waitingOn: 'athlete',
      isComplete: false,
      pendingReviewMilestone: null,
      milestones: sortedMilestones,
    }
  }

  // The brand's own step: ship the product, prepare or confirm the appearance, approve a draft
  if (isBrandStep(currentMilestone)) {
    return {
      currentMilestone,
      actionOwner: 'marketer',
      actionRequired: `Complete "${currentMilestone.name}"`,
      waitingOn: 'marketer',
      isComplete: false,
      pendingReviewMilestone: null,
      milestones: sortedMilestones,
    }
  }

  // Check if this milestone requires approval but hasn't been submitted yet
  if (currentMilestone.requiresApproval || currentMilestone.requires_approval) {
    return {
      currentMilestone,
      actionOwner: 'athlete',
      actionRequired: `Submit for "${currentMilestone.name}"`,
      waitingOn: 'athlete',
      isComplete: false,
      pendingReviewMilestone: null,
      milestones: sortedMilestones,
    }
  }

  // Milestone doesn't require approval (auto-complete type)
  return {
    currentMilestone,
    actionOwner: 'athlete',
    actionRequired: `Complete the "${currentMilestone.name}" step`,
    waitingOn: 'athlete',
    isComplete: false,
    pendingReviewMilestone: null,
    milestones: sortedMilestones,
  }
}

/**
 * Get a user-friendly description of the current status for the athlete
 */
export function getAthleteStatusDisplay(state: CollaborationState): StatusDisplay {
  if (state.isComplete) {
    return {
      title: '🎉 Collaboration Complete',
      description: 'Great work! This collaboration has been completed.',
      actionButton: null,
      variant: 'success',
    }
  }

  if (state.actionOwner === 'marketer') {
    return {
      title: '⏳ Waiting for Review',
      description: `Your submission for "${state.currentMilestone?.name}" is being reviewed by the brand. You'll be notified when they respond.`,
      actionButton: null,
      variant: 'waiting',
    }
  }

  if (state.actionOwner === 'athlete') {
    const isRevision = state.currentMilestone?.status === 'revision_requested'
    return {
      title: '🔔 Action Required',
      description: state.actionRequired || '',
      actionButton: isRevision ? 'Revise Submission' : 'Continue',
      variant: 'action',
    }
  }

  if (!state.isComplete && !state.currentMilestone && !state.actionOwner && state.actionRequired) {
    return {
      title: '⏳ Waiting for Brand',
      description: state.actionRequired,
      actionButton: null,
      variant: 'waiting',
    }
  }

  return {
    title: 'Status Unknown',
    description: 'Unable to determine current status.',
    actionButton: null,
    variant: 'unknown',
  }
}

/**
 * Get a user-friendly description of the current status for the marketer
 */
export function getMarketerStatusDisplay(
  state: CollaborationState,
  athleteName = 'The athlete',
): StatusDisplay {
  if (state.isComplete) {
    return {
      title: 'Collaboration Complete',
      description: `${athleteName} finished every step of this deal.`,
      actionButton: null,
      variant: 'success',
    }
  }

  if (state.actionOwner === 'marketer' && isBrandStep(state.currentMilestone)) {
    return {
      title: '🟢 Your move',
      description: `${athleteName} is waiting on you: "${state.currentMilestone?.name}".`,
      actionButton: null,
      variant: 'action',
    }
  }

  if (state.actionOwner === 'marketer' && state.pendingReviewMilestone) {
    return {
      title: '🔔 Review Required',
      description: `${athleteName} submitted for "${state.pendingReviewMilestone.name}". Ready for your review.`,
      actionButton: 'Review Submission',
      variant: 'action',
    }
  }

  if (state.actionOwner === 'athlete') {
    // Special case: waiting for athlete to accept/decline offer
    if (!state.currentMilestone && state.actionRequired === 'Accept or decline the offer') {
      return {
        title: '⏳ Offer Sent',
        description: `${athleteName} can accept, decline, or negotiate the offer terms with you.`,
        actionButton: 'Negotiate Terms',
        variant: 'waiting',
      }
    }

    // Append a status-line suffix to the existing description so the surface
    // doesn't morph between states the user is tracking. Three cases:
    //   - athlete confirmed on track  → green check + relative time
    //   - active check-in waiting     → "we nudged them" reassurance
    //   - neither                     → no suffix
    const checkIn = state.currentMilestone?.checkIn
    let checkInSuffix = ''
    if (checkIn?.response === 'on_track' && checkIn.responseAt) {
      checkInSuffix = ` ✓ Confirmed on track ${formatCheckInRelative(checkIn.responseAt)}.`
    } else if (checkIn?.active) {
      checkInSuffix =
        " ⏳ We've sent a status check-in — waiting on the athlete to confirm they're on track."
    }
    const onTrackSuffix = checkInSuffix

    const isRevision = state.currentMilestone?.status === 'revision_requested'
    if (isRevision) {
      return {
        title: '⏳ Waiting for Revision',
        description:
          `${athleteName} is working on revisions for "${state.currentMilestone?.name}".` +
          onTrackSuffix,
        actionButton: null,
        variant: 'waiting',
      }
    }

    return {
      title: '⏳ Waiting on Athlete',
      description:
        `${athleteName} is working toward "${state.currentMilestone?.name}".` + onTrackSuffix,
      actionButton: null,
      variant: 'waiting',
    }
  }

  return {
    title: 'Status Unknown',
    description: 'Unable to determine current status.',
    actionButton: null,
    variant: 'unknown',
  }
}
